import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  DollarSign, 
  Mail, 
  Users, 
  TrendingUp, 
  Eye,
  MessageSquare,
  Calendar
} from 'lucide-react';

interface DashboardStats {
  totalBlogPosts: number;
  totalPricingPackages: number;
  totalContactForms: number;
  totalNewsletterSubscribers: number;
  recentActivity: Array<{
    id: string;
    type: string;
    message: string;
    timestamp: string;
  }>;
}

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalBlogPosts: 0,
    totalPricingPackages: 0,
    totalContactForms: 0,
    totalNewsletterSubscribers: 0,
    recentActivity: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/dashboard/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        // Mock data for demo purposes when API is not connected
        setStats({
          totalBlogPosts: 12,
          totalPricingPackages: 5,
          totalContactForms: 28,
          totalNewsletterSubscribers: 156,
          recentActivity: [
            {
              id: '1',
              type: 'contact',
              message: 'New contact form submission from John Doe',
              timestamp: '2 hours ago'
            },
            {
              id: '2',
              type: 'newsletter',
              message: 'New newsletter subscriber: jane@example.com',
              timestamp: '4 hours ago'
            },
            {
              id: '3',
              type: 'blog',
              message: 'Blog post "Modern Web Design Trends" was published',
              timestamp: '1 day ago'
            },
            {
              id: '4',
              type: 'pricing',
              message: 'Updated pricing package: Premium Package',
              timestamp: '2 days ago'
            }
          ]
        });
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Blog Posts',
      value: stats.totalBlogPosts,
      icon: FileText,
      color: 'bg-blue-500',
      href: '/admin/blog'
    },
    {
      title: 'Pricing Packages',
      value: stats.totalPricingPackages,
      icon: DollarSign,
      color: 'bg-green-500',
      href: '/admin/pricing'
    },
    {
      title: 'Contact Forms',
      value: stats.totalContactForms,
      icon: Mail,
      color: 'bg-yellow-500',
      href: '/admin/contacts'
    },
    {
      title: 'Newsletter Subscribers',
      value: stats.totalNewsletterSubscribers,
      icon: Users,
      color: 'bg-purple-500',
      href: '/admin/newsletter'
    }
  ];

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'contact':
        return Mail;
      case 'newsletter':
        return Users;
      case 'blog':
        return FileText;
      case 'pricing':
        return DollarSign;
      default:
        return MessageSquare;
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Welcome back! Here's what's happening with your website.
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.title}
              className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow duration-200"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`${card.color} p-3 rounded-md`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {card.title}
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {card.value}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-5 py-3">
                <div className="text-sm">
                  <a 
                    href={card.href} 
                    className="font-medium text-blue-600 hover:text-blue-500"
                  >
                    View all
                  </a>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Activity */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Recent Activity
            </h3>
            <div className="flow-root">
              <ul className="-mb-8">
                {stats.recentActivity.map((activity, activityIdx) => {
                  const Icon = getActivityIcon(activity.type);
                  return (
                    <li key={activity.id}>
                      <div className="relative pb-8">
                        {activityIdx !== stats.recentActivity.length - 1 ? (
                          <span
                            className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                            aria-hidden="true"
                          />
                        ) : null}
                        <div className="relative flex space-x-3">
                          <div>
                            <span className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center ring-8 ring-white">
                              <Icon className="h-4 w-4 text-gray-500" />
                            </span>
                          </div>
                          <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                            <div>
                              <p className="text-sm text-gray-500">
                                {activity.message}
                              </p>
                            </div>
                            <div className="text-right text-sm whitespace-nowrap text-gray-500">
                              <time>{activity.timestamp}</time>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Quick Actions
            </h3>
            <div className="space-y-3">
              <a
                href="/admin/blog/new"
                className="w-full flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <FileText className="h-5 w-5 text-gray-400 mr-3" />
                Create New Blog Post
              </a>
              <a
                href="/admin/pricing"
                className="w-full flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <DollarSign className="h-5 w-5 text-gray-400 mr-3" />
                Manage Pricing
              </a>
              <a
                href="/admin/contacts"
                className="w-full flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Mail className="h-5 w-5 text-gray-400 mr-3" />
                View Contact Messages
              </a>
              <a
                href="/admin/newsletter"
                className="w-full flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Users className="h-5 w-5 text-gray-400 mr-3" />
                Manage Newsletter
              </a>
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Eye className="h-5 w-5 text-gray-400 mr-3" />
                View Website
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Metrics (Placeholder) */}
      <div className="mt-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Website Analytics
              </h3>
              <TrendingUp className="h-5 w-5 text-gray-400" />
            </div>
            <div className="text-center py-12">
              <Calendar className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                Analytics Integration
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Connect Google Analytics or other analytics tools to see website performance metrics.
              </p>
              <div className="mt-6">
                <button
                  type="button"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Setup Analytics
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;